import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/integritetspolicy")({
  component: Privacy,
  head: () => ({
    meta: [
      { title: "Integritetspolicy – Alkoholprovet" },
      {
        name: "description",
        content:
          "Så här behandlar Alkoholprovet dina personuppgifter enligt GDPR: vilka uppgifter, ändamål, laglig grund, lagringstid, mottagare, överföringar och dina rättigheter.",
      },
      { property: "og:title", content: "Integritetspolicy – Alkoholprovet" },
      {
        property: "og:description",
        content:
          "Så behandlar Alkoholprovet dina personuppgifter enligt GDPR: ändamål, laglig grund, lagringstid, mottagare och dina rättigheter.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://alkoholprovet.se/integritetspolicy" },
    ],

    links: [{ rel: "canonical", href: "https://alkoholprovet.se/integritetspolicy" }],
  }),
});

function Privacy() {
  return (
    <article className="container-narrow py-14">
      <h1 className="font-display text-4xl font-bold">Integritetspolicy</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Senast uppdaterad: {new Date().toLocaleDateString("sv-SE")}
      </p>
      <p className="mt-6 text-muted-foreground leading-relaxed">
        Den här policyn beskriver hur Alkoholprovet behandlar dina
        personuppgifter när du använder webbplatsen alkoholprovet.se och våra
        digitala övningsprov. Vi följer EU:s dataskyddsförordning (GDPR) och
        svensk dataskyddslagstiftning.
      </p>

      <Section title="Personuppgiftsansvarig">
        Personuppgiftsansvarig är den juridiska person som driver
        Alkoholprovet. Kontakta oss via{" "}
        <Link to="/kontakt" className="underline">
          kontaktsidan
        </Link>{" "}
        eller e-post <a className="underline" href="mailto:hej@alkoholprovet.se">hej@alkoholprovet.se</a> för
        frågor om denna policy eller dina personuppgifter.
      </Section>

      <Section title="Personuppgifter vi behandlar">
        <ul className="list-disc pl-5 space-y-1">
          <li>Kontouppgifter: e-postadress, namn (valfritt) och krypterat lösenord.</li>
          <li>Användningsdata: resultat på övningsprov, framsteg, inloggningstid.</li>
          <li>Betalningsdata: orderhistorik och kvitto-id. Kortuppgifter hanteras direkt av Stripe – vi lagrar inte kortnummer.</li>
          <li>Kommunikation: meddelanden som du skickar via kontaktformulär eller e-post.</li>
          <li>Tekniska data: IP-adress, webbläsare, enhetstyp och cookies (se nedan).</li>
        </ul>
      </Section>

      <Section title="Ändamål och laglig grund">
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>Tillhandahålla tjänsten</strong> (skapa konto, leverera
            övningsprov, hantera premium) – laglig grund: fullgörande av avtal
            (art. 6.1 b GDPR).
          </li>
          <li>
            <strong>Betalning och fakturering</strong> – fullgörande av avtal
            och rättslig förpliktelse (bokföringslagen, art. 6.1 b och c).
          </li>
          <li>
            <strong>Säkerhet, missbruksskydd och felsökning</strong> –
            berättigat intresse (art. 6.1 f).
          </li>
          <li>
            <strong>Produktförbättring och anonymiserad statistik</strong> –
            berättigat intresse, eller samtycke när cookies krävs (art. 6.1 a
            och f).
          </li>
          <li>
            <strong>Marknadsföring och konverteringsmätning</strong> – endast
            efter ditt samtycke (art. 6.1 a och 6 kap. § lagen om elektronisk
            kommunikation).
          </li>
        </ul>
      </Section>

      <Section title="Cookies och liknande teknik">
        Vi sätter en cookie-banner första gången du besöker webbplatsen.
        <ul className="list-disc pl-5 space-y-1 mt-2">
          <li>
            <strong>Nödvändiga cookies:</strong> krävs för inloggning,
            betalning och säkerhet. Kan inte stängas av.
          </li>
          <li>
            <strong>Statistik (Plausible Analytics):</strong> cookie-fri och
            insamlar inga personuppgifter; används utan samtycke.
          </li>
          <li>
            <strong>Marknadsföring (Google Ads/Google Tag):</strong> aktiveras
            endast om du klickar "Acceptera alla". Vi använder Google Consent
            Mode v2 så att inga annons-cookies sätts utan ditt samtycke. Du kan
            återkalla samtycket när som helst genom att rensa cookies för
            alkoholprovet.se.
          </li>
        </ul>
      </Section>

      <Section title="Mottagare och personuppgiftsbiträden">
        Vi delar uppgifter med följande leverantörer som behandlar data för vår
        räkning enligt biträdesavtal:
        <ul className="list-disc pl-5 space-y-1 mt-2">
          <li>Lovable Cloud / Supabase (hosting, databas, autentisering) – EU.</li>
          <li>Stripe (betalningar) – EU/USA, baserat på EU:s standardavtalsklausuler.</li>
          <li>Mailgun (transaktionella e-postmeddelanden) – EU.</li>
          <li>Plausible Analytics (cookie-fri webbstatistik) – EU.</li>
          <li>Google Ireland Ltd. (Google Ads, konverteringsspårning) – endast efter samtycke. Överföring till USA sker via EU:s standardavtalsklausuler och Data Privacy Framework.</li>
        </ul>
      </Section>

      <Section title="Överföring till tredje land">
        Vissa leverantörer (Stripe, Google) kan behandla data i USA. Sådan
        överföring sker endast med lämpliga skyddsåtgärder enligt art. 46 GDPR
        (EU-kommissionens standardavtalsklausuler och, där tillämpligt, EU–US
        Data Privacy Framework).
      </Section>

      <Section title="Lagringstid">
        <ul className="list-disc pl-5 space-y-1">
          <li>Kontodata sparas så länge ditt konto är aktivt. Inaktiva konton kan raderas efter 24 månader.</li>
          <li>Bokföringsunderlag sparas i 7 år enligt bokföringslagen.</li>
          <li>Supportärenden raderas senast 24 månader efter ärendet stängts.</li>
          <li>Cookies har egna livslängder; samtyckesvalet sparas i 12 månader.</li>
        </ul>
      </Section>

      <Section title="Dina rättigheter">
        Du har rätt att
        <ul className="list-disc pl-5 space-y-1 mt-2">
          <li>begära ett registerutdrag över de uppgifter vi behandlar om dig,</li>
          <li>få felaktiga uppgifter rättade,</li>
          <li>begära radering av dina uppgifter (rätten att bli glömd),</li>
          <li>begära begränsning av eller invända mot behandlingen,</li>
          <li>få ut dina uppgifter i ett maskinläsbart format (dataportabilitet),</li>
          <li>när som helst återkalla samtycke utan att det påverkar lagligheten av tidigare behandling.</li>
        </ul>
        Kontakta oss via{" "}
        <a className="underline" href="mailto:hej@alkoholprovet.se">hej@alkoholprovet.se</a>{" "}
        för att utöva dina rättigheter. Vi svarar normalt inom 30 dagar.
      </Section>

      <Section title="Klagomål">
        Du har rätt att lämna klagomål till Integritetsskyddsmyndigheten (IMY),
        Box 8114, 104 20 Stockholm,{" "}
        <a className="underline" href="https://www.imy.se" target="_blank" rel="noreferrer noopener">
          imy.se
        </a>
        , om du anser att vi behandlar dina personuppgifter i strid med GDPR.
      </Section>

      <Section title="Säkerhet">
        Vi använder TLS-kryptering, lösenordskryptering, åtkomstkontroller och
        loggning. Behörighet till data är begränsad till personal som behöver
        den för sitt arbete.
      </Section>

      <Section title="Ändringar i policyn">
        Vi kan komma att uppdatera denna policy. Den senaste versionen finns
        alltid på den här sidan med datum längst upp.
      </Section>
    </article>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="font-display text-xl font-semibold">{title}</h2>
      <div className="mt-2 text-muted-foreground leading-relaxed">{children}</div>
    </section>
  );
}
